export type ImportedGoalPlan = {
  primaryGoal: {
    goal_purpose?: 'technical_cte' | 'personal_professional';
    title?: string;
    why_text?: string;
    target_metric_text?: string;
  };
  dailyPlan: {
    plan_track?: string;
    plan_target?: string;
    plan_first_step?: string;
    plan_success_criteria?: string;
    protected_goal_minutes?: string;
    planned_secondary_work?: string;
    planned_work_mode?: string;
  };
  support: {
    support_mode?: 'adapted' | 'teacher_stated';
    student_visible_rationale?: string;
  };
  importedFields: string[];
  warnings: string[];
};

const MAX_TEXT_LENGTH = 500;
const MAX_GOAL_TITLE_LENGTH = 255;
const PLAN_TRACKS = new Set(['python', 'comptia', 'web_development', 'main_project', 'secondary_project', 'missing_work', 'other']);
const WORK_MODES = new Set(['five_week_goal', 'nerd_herd', 'live_lesson', 'personal_project', 'required_recovery', 'exploration', 'transition_cleanup']);

const normalizeKey = (value: unknown) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/['’]/g, '')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const cleanText = (value: unknown, limit = MAX_TEXT_LENGTH) => String(value ?? '')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, limit);

const PROTECTED_KEYS = new Set([
  'student', 'student_id', 'student_name', 'name', 'first_name', 'last_name',
  'date_of_birth', 'dob', 'eligibility', 'eligibility_category', 'disability',
  'diagnosis', 'medical', 'health', 'iep', 'iep_date', 'family', 'parent', 'guardian',
]);

const KEY_ALIASES: Record<string, string> = {
  goal: 'title',
  goal_title: 'title',
  primary_goal: 'title',
  primary_goal_title: 'title',
  title: 'title',
  why: 'why_text',
  why_it_matters: 'why_text',
  goal_why: 'why_text',
  goal_why_text: 'why_text',
  progress_target: 'target_metric_text',
  observable_progress_target: 'target_metric_text',
  target_metric: 'target_metric_text',
  target_metric_text: 'target_metric_text',
  goal_progress_target: 'target_metric_text',
  goal_purpose: 'goal_purpose',
  purpose: 'goal_purpose',
  daily_task: 'plan_target',
  todays_task: 'plan_target',
  today_s_task: 'plan_target',
  plan_target: 'plan_target',
  daily_plan: 'plan_target',
  first_step: 'plan_first_step',
  plan_first_step: 'plan_first_step',
  success_check: 'plan_success_criteria',
  success_criteria: 'plan_success_criteria',
  plan_success_criteria: 'plan_success_criteria',
  daily_track: 'plan_track',
  plan_track: 'plan_track',
  protected_minutes: 'protected_goal_minutes',
  protected_goal_minutes: 'protected_goal_minutes',
  next_work: 'planned_secondary_work',
  what_comes_next: 'planned_secondary_work',
  planned_secondary_work: 'planned_secondary_work',
  work_mode: 'planned_work_mode',
  planned_work_mode: 'planned_work_mode',
  support_level: 'support_mode',
  support_mode: 'support_mode',
  support_reason: 'student_visible_rationale',
  student_visible_reason: 'student_visible_rationale',
  student_visible_rationale: 'student_visible_rationale',
};

const parseCsv = (source: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    if (character === '"') {
      if (quoted && source[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      row.push(cell);
      cell = '';
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && source[index + 1] === '\n') index += 1;
      row.push(cell);
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += character;
    }
  }
  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
};

const flattenObject = (value: Record<string, any>): Array<[string, unknown]> => {
  const rows: Array<[string, unknown]> = [];
  Object.entries(value).forEach(([key, entry]) => {
    const normalized = normalizeKey(key);
    if (entry && typeof entry === 'object' && !Array.isArray(entry) && ['primary_goal', 'daily_plan', 'support'].includes(normalized)) {
      Object.entries(entry).forEach(([nestedKey, nestedValue]) => rows.push([nestedKey, nestedValue]));
    } else {
      rows.push([key, entry]);
    }
  });
  return rows;
};

const sourcePairs = (source: string): Array<[string, unknown]> => {
  const trimmed = source.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('{')) {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('The JSON import must contain one object.');
    return flattenObject(parsed);
  }

  const csvRows = parseCsv(trimmed);
  if (csvRows.length >= 2 && csvRows[0].length > 2) {
    return csvRows[0].map((header, index) => [header, csvRows[1]?.[index] ?? '']);
  }
  if (csvRows.length > 0 && csvRows.every((row) => row.length === 2)) {
    return csvRows.map((row) => [row[0], row[1]]);
  }

  return trimmed.split(/\r?\n/).flatMap((line) => {
    const match = line.match(/^\s*([^:|]+)\s*[:|]\s*(.*?)\s*$/);
    return match ? [[match[1], match[2]] as [string, unknown]] : [];
  });
};

export const parseGoalPlanImport = (source: string): ImportedGoalPlan => {
  const result: ImportedGoalPlan = {
    primaryGoal: {},
    dailyPlan: {},
    support: {},
    importedFields: [],
    warnings: [],
  };
  let pairs: Array<[string, unknown]>;
  try {
    pairs = sourcePairs(source);
  } catch (error: any) {
    throw new Error(error?.message || 'The import could not be read.');
  }
  if (!pairs.length) throw new Error('No recognized labeled, JSON, or CSV fields were found.');

  pairs.forEach(([rawKey, rawValue]) => {
    const key = normalizeKey(rawKey);
    if (PROTECTED_KEYS.has(key)) {
      result.warnings.push(`Ignored protected field: ${String(rawKey).trim()}.`);
      return;
    }
    const field = KEY_ALIASES[key];
    if (!field) return;
    const text = cleanText(rawValue, field === 'title' ? MAX_GOAL_TITLE_LENGTH : MAX_TEXT_LENGTH);
    if (!text) return;

    if (field === 'goal_purpose') {
      const token = normalizeKey(text);
      const purpose = token.includes('technical') || token === 'cte'
        ? 'technical_cte'
        : token.includes('personal') || token.includes('professional')
          ? 'personal_professional'
          : null;
      if (!purpose) {
        result.warnings.push('Goal purpose was ignored because it was not technical/CTE or personal/professional.');
        return;
      }
      result.primaryGoal.goal_purpose = purpose;
    } else if (field === 'support_mode') {
      const token = normalizeKey(text);
      result.support.support_mode = token.includes('state') || token.includes('complete')
        ? 'teacher_stated'
        : 'adapted';
    } else if (field === 'protected_goal_minutes') {
      const minutes = Number.parseInt(text, 10);
      if (!Number.isFinite(minutes) || minutes < 5 || minutes > 90) {
        result.warnings.push('Protected minutes were ignored because they must be from 5 to 90.');
        return;
      }
      result.dailyPlan.protected_goal_minutes = String(minutes);
    } else if (field === 'plan_track') {
      const track = normalizeKey(text);
      if (!PLAN_TRACKS.has(track)) {
        result.warnings.push(`Plan track was ignored because “${text}” is not supported.`);
        return;
      }
      result.dailyPlan.plan_track = track;
    } else if (field === 'planned_work_mode') {
      const workMode = normalizeKey(text);
      if (!WORK_MODES.has(workMode)) {
        result.warnings.push(`Work mode was ignored because “${text}” is not supported.`);
        return;
      }
      result.dailyPlan.planned_work_mode = workMode;
    } else if (['title', 'why_text', 'target_metric_text'].includes(field)) {
      (result.primaryGoal as Record<string, string>)[field] = text;
    } else if (field === 'student_visible_rationale') {
      result.support.student_visible_rationale = text;
    } else {
      (result.dailyPlan as Record<string, string>)[field] = text;
    }
    result.importedFields.push(field);
  });

  result.importedFields = Array.from(new Set(result.importedFields));
  result.warnings = Array.from(new Set(result.warnings));
  if (!result.importedFields.length) throw new Error('No instructional goal or daily-plan fields were recognized.');
  return result;
};
